import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { setCurrentPlatformData } from './data/store/actions/platform.actions';
import { CurrentPlatform } from './shared/enums';
import {
  getPlatformFromViewport,
  subscribeToViewportPlatform,
} from './shared/utils/platform.utils';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { interval, filter, startWith } from 'rxjs';
import monitoring from 'src/utils/monitoring';
import { IonicModule } from '@ionic/angular';
import { ImageComponent } from './shared/components/image/image.component';
import {
  firebaseMessagingSwScope,
  firebaseMessagingSwUrl,
} from './shared/constants/firebase.config';

@Component({
  standalone: true,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, IonicModule, ImageComponent],
})
export class AppComponent implements OnInit, OnDestroy {
  private _store = inject(Store);
  private _swUpdate = inject(SwUpdate);
  private _router = inject(Router);

  title = 'Befree social';

  private _unsubscribeViewport: (() => void) | null = null;
  private _cleanupUpdateListeners: (() => void)[] = [];

  private readonly _onSwMessage = (event: MessageEvent): void => {
    const payload = event.data;
    if (
      !payload ||
      typeof payload !== 'object' ||
      payload.type !== 'BEFREE_PUSH_NAVIGATE' ||
      typeof payload.path !== 'string'
    ) {
      return;
    }
    if (!payload.path.startsWith('/') || payload.path.startsWith('//')) {
      return;
    }
    let pathWithQuery: string;
    try {
      const nextHref = new URL(payload.path, window.location.origin).href;
      const currentLocation = new URL(window.location.href);
      const targetLocation = new URL(nextHref);
      if (
        currentLocation.origin === targetLocation.origin &&
        currentLocation.pathname === targetLocation.pathname &&
        currentLocation.search === targetLocation.search
      ) {
        return;
      }
      pathWithQuery = targetLocation.pathname + targetLocation.search + targetLocation.hash;
    } catch {
      return;
    }
    void this._router
      .navigateByUrl(pathWithQuery)
      .then((ok) => {
        if (!ok) {
          window.location.assign(new URL(pathWithQuery, window.location.origin).href);
        }
      })
      .catch(() => {
        window.location.assign(new URL(pathWithQuery, window.location.origin).href);
      });
  };

  isPWABannerVisible = true;

  isPWAInstallModalOpen = false;

  ngOnInit(): void {
    this._unsubscribeViewport = subscribeToViewportPlatform((platform) => {
      this._store.dispatch(setCurrentPlatformData({ platform }));
    });

    this._setupServiceWorkerRefresh();

    if (typeof navigator !== 'undefined' && navigator.serviceWorker?.addEventListener) {
      navigator.serviceWorker.addEventListener('message', this._onSwMessage);
      this._cleanupUpdateListeners.push(() =>
        navigator.serviceWorker.removeEventListener('message', this._onSwMessage),
      );
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeViewport?.();
    this._cleanupUpdateListeners.forEach((cleanup) => cleanup());
    this._cleanupUpdateListeners = [];
  }

  get isPWAAvailable(): boolean {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') {
      return false;
    }
    if (!('serviceWorker' in navigator)) {
      return false;
    }
    if (this._isInstalledPwaExperience()) {
      return false;
    }
    return getPlatformFromViewport() === CurrentPlatform.mobile;
  }

  openPWAInstall(): void {
    this.isPWAInstallModalOpen = true;
  }

  closePWAInstallModal(): void {
    this.isPWAInstallModalOpen = false;
  }

  closePWABanner(): void {
    this.isPWABannerVisible = false;
  }

  private _setupServiceWorkerRefresh(): void {
    const serviceWorkerContainer =
      typeof navigator !== 'undefined' && navigator.serviceWorker ? navigator.serviceWorker : null;

    const refreshFirebaseMessagingWorker = () => {
      if (!serviceWorkerContainer) {
        return;
      }
      void serviceWorkerContainer
        .register(firebaseMessagingSwUrl, {
          scope: firebaseMessagingSwScope,
          updateViaCache: 'none',
        })
        .then((registration) => void registration.update().catch(() => undefined))
        .catch(() => undefined);
    };

    const checkAngularServiceWorkerUpdate = () => {
      if (!this._swUpdate.isEnabled) {
        return;
      }
      void this._swUpdate
        .checkForUpdate()
        .catch((error) => console.error('PWA update check failed', error));
    };

    const refreshWorkersOnUserReturn = () => {
      refreshFirebaseMessagingWorker();
      checkAngularServiceWorkerUpdate();
    };

    refreshFirebaseMessagingWorker();

    const onDocumentVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshWorkersOnUserReturn();
      }
    };
    document.addEventListener('visibilitychange', onDocumentVisibilityChange);
    const onWindowFocus = () => refreshWorkersOnUserReturn();
    const onPageShow = () => refreshWorkersOnUserReturn();
    window.addEventListener('focus', onWindowFocus);
    window.addEventListener('pageshow', onPageShow);
    this._cleanupUpdateListeners.push(
      () => document.removeEventListener('visibilitychange', onDocumentVisibilityChange),
      () => window.removeEventListener('focus', onWindowFocus),
      () => window.removeEventListener('pageshow', onPageShow),
    );

    if (!this._swUpdate.isEnabled) {
      return;
    }

    this._swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe(() => {
        void this._swUpdate
          .activateUpdate()
          .then(() => document.location.reload())
          .catch((error) => monitoring.catchError(error));
      });

    interval(60_000)
      .pipe(startWith(0))
      .subscribe(() => {
        checkAngularServiceWorkerUpdate();
      });
  }

  private _isInstalledPwaExperience(): boolean {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return false;
    }
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }
    const nav = navigator as Navigator & { standalone?: boolean };
    return nav.standalone === true;
  }
}
